import { FALLBACK_LANGUAGE, LANGUAGES } from '@muzly/shared/i18n';

import { privacy20260918En } from './documents/privacy/2026-09-18.en';
import { privacy20260918Ko } from './documents/privacy/2026-09-18.ko';
import { terms20260922En } from './documents/terms/2026-09-22.en';
import { terms20260922Ko } from './documents/terms/2026-09-22.ko';

import type { PolicyDoc } from './policyDoc';
import type { Language } from '@muzly/shared/i18n';

/**
 * 약관·방침의 **시행일별 버전 목록** — 화면의 시행일 알약이 이 목록을 그대로 보여 준다.
 *
 * 문서는 서버가 아니라 레포에 있다: 법적 문서는 PR 로 고쳐야 누가 언제 무엇을 바꿨는지 남고,
 * 앱을 열 수 없는 사람(심사·앱 밖 브라우저)도 같은 글을 본다.
 *
 * **개정본을 더하는 법** — `tools/make-policy-docs.py` 머리말. 여기서는 그 시행일을 목록에
 * 한 줄 더한다. 옛 버전은 지우지 않는다 — 지난 시행일을 고르면 그때의 글이 나와야 한다.
 */

export const POLICY_KINDS = ['terms', 'privacy'] as const;
export type PolicyKind = (typeof POLICY_KINDS)[number];

/**
 * 문서가 있는 언어 — 앱이 고를 수 있는 언어(`LANGUAGES`)보다 좁다. 법적 문서는 원본이 있는
 * 말로만 낸다(번역본을 지어내지 않는다). 원본이 생기면 여기와 `POLICIES` 에 더한다.
 */
export const POLICY_LANGUAGES = ['ko', 'en'] as const satisfies Language[];
export type PolicyLanguage = (typeof POLICY_LANGUAGES)[number];

/** 문서가 없는 앱 언어가 읽을 문서 — 중국어 화면에서는 한국어보다 영어가 읽힌다. */
const POLICY_LANGUAGE_FOR: Record<Language, PolicyLanguage> = {
  ko: 'ko',
  en: 'en',
  zh: 'en',
};

export interface PolicyVersion {
  /** 시행일 `YYYY-MM-DD` — 버전의 이름이자 주소의 `?v=` 값. */
  effective: string;
  /** 문서 언어마다 하나 — 문서 언어가 늘면 문서도 같이 있어야 타입이 선다. */
  docs: Record<PolicyLanguage, PolicyDoc>;
}

export type PolicyRegistry = Record<PolicyKind, PolicyVersion[]>;

export const POLICIES: PolicyRegistry = {
  terms: [
    {
      effective: '2026-09-22',
      docs: { ko: terms20260922Ko, en: terms20260922En },
    },
  ],
  privacy: [
    {
      effective: '2026-09-18',
      docs: { ko: privacy20260918Ko, en: privacy20260918En },
    },
  ],
};

/**
 * 문서의 언어 — 앞에서부터 처음 맞는 후보(앱이 고를 수 있는 언어)가 읽을 문서이고, 없으면
 * 앱의 기본 언어다. 후보는 주소의 `?lang=`·화면 언어처럼 밖에서 온 값이라 무엇이든 올 수 있다.
 */
export const pickPolicyLanguage = (
  ...candidates: (string | null | undefined)[]
): PolicyLanguage =>
  POLICY_LANGUAGE_FOR[
    candidates.find((candidate): candidate is Language =>
      LANGUAGES.some((lang) => lang === candidate),
    ) ?? FALLBACK_LANGUAGE
  ];

/** 최신 시행일이 먼저 — `YYYY-MM-DD` 는 글자 순서가 곧 날짜 순서다. */
export const policyVersions = (
  kind: PolicyKind,
  registry: PolicyRegistry = POLICIES,
): PolicyVersion[] =>
  [...registry[kind]].sort((a, b) => b.effective.localeCompare(a.effective));

/**
 * 보여 줄 버전 하나. 시행일을 안 주거나 모르는 시행일이면 **최신**이다 — 낡은 링크의
 * `?v=` 가 빈 화면이 되면 안 된다. 그 종류에 버전이 하나도 없을 때만 `null`.
 */
export const resolvePolicy = (
  kind: PolicyKind,
  effective: string | null | undefined,
  registry: PolicyRegistry = POLICIES,
): PolicyVersion | null => {
  const versions = policyVersions(kind, registry);
  return (
    versions.find((version) => version.effective === effective) ??
    versions[0] ??
    null
  );
};

/** 시행일 알약의 글자 — `2026. 09. 22 시행` / `Effective Sep 22, 2026`. */
export const formatEffective = (
  effective: string,
  lang: PolicyLanguage,
): string => {
  const [year, month, day] = effective.split('-');
  if (lang === 'ko') {
    return `${year}. ${month}. ${day} 시행`;
  }
  // 정오 UTC — 어느 시간대에서 그려도 날짜가 하루 밀리지 않는다.
  const date = new Date(`${effective}T12:00:00Z`);
  const text = new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  }).format(date);
  return `Effective ${text}`;
};
